import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, message, Space, Tag, Tooltip } from "antd";
import _ from "lodash";
import moment from "moment";
import { useContext, useState } from "react";
import Highlighter from "react-highlight-words";
import apiPaths from "../../api/paths";
import DynamicTable from "../../components/DynamicTable";
import BatteryModal from "./components/BatteryModal";
import { batteryApiRequest, BatteryContext } from "./common";
import ValidityModal from "./components/ValidityModal";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import BatteryMarkers from "./components/BatteryMarkers";
import "leaflet/dist/leaflet.css";

const DEFAULT_COLUMNS = {
  battery_number: {
    show: true,
    label: "Battery Numbere",
    key: "battery_number",
  },
  imei_number: {
    show: true,
    label: "IMEI Number",
    key: "imei_number",
  },
  battery_voltage: {
    show: true,
    label: "Voltage",
    key: "battery_voltage",
  },
  type: {
    show: false,
    label: "Type",
    key: "type",
  },
  location: {
    show: true,
    label: "Current Location",
    key: "location",
  },
  mfg_date: {
    show: false,
    label: "MFG Dat",
    key: "mfg_date",
  },
  lock_status: {
    show: true,
    label: "Lock Status",
    key: "lock_status",
  },
};

function Batteries({ user }) {
  const [editingKey, setEditingKey] = useState(null);
  const [activeBattery, setActiveBattery] = useState(null);

  const { batteries, setBatteries } = useContext(BatteryContext);
  const [showBatteryModal, setShowBatteryModal] = useState(false);
  const [showValidityModal, setShowValidityModal] = useState(false);
  const allowEdit = user.is_admin;

  const handleBatteryLock = async (batteryId, status) => {
    const res = await batteryApiRequest({
      path: apiPaths.batteries + `/${batteries[batteryId].battery_number}/lock`,
      method: "POST",
      data: {
        lock: status,
      },
      token: user.token,
    });
    if (!res.error) {
      let updatedBatteries = { ...batteries };
      updatedBatteries[batteryId].lock_status = status;
      setBatteries(updatedBatteries);
      return;
    }
    message.error(res.error_description);
  };

  const COLUMNS = [
    {
      title: "B/N",
      dataIndex: "battery_number",
      fixed: "left",
      width: 100,
      filterType: "search",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <a
            onClick={() => {
              if (record.location?.coordinates) setActiveBattery(record);
            }}
          >
            <Highlighter
              highlightStyle={{
                backgroundColor: "#ffc069",
                padding: 0,
              }}
              searchWords={[]}
              autoEscape
              textToHighlight={text ? text.toString() : ""}
            />
          </a>
        </div>
      ),
    },
    {
      title: "IMEI Number",
      dataIndex: "imei_number",
      width: 170,
      filterType: "search",
    },
    {
      title: "Voltage",
      dataIndex: "battery_voltage",
      width: 90,
      sorter: (a, b) => a.battery_voltage - b.battery_voltage,
      render: (value, record) => {
        return (
          <div>
            <div style={{ color: value < 39 ? "red" : "green" }}>
              {value + " V"}
            </div>
            <div style={{ fontSize: 10 }}>
              {record.timestamp
                ? moment(record.timestamp.replace("Z", "")).fromNow()
                : "Un Known"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 70,
    },
    {
      title: "Valid till",
      dataIndex: "valid_till",
      width: "200px",
      render: (_, record) => {
        return (
          <>
            {record.valid_till
              ? moment(record.valid_till).format("DD-MM-YYYY HH:mm")
              : "Not Given"}
            {"  "}{" "}
            <Button
              type="link"
              onClick={() => {
                setEditingKey(record._id);
                setShowValidityModal(true);
              }}
            >
              <EditOutlined style={{ fontSize: 18 }} />
            </Button>
          </>
        );
      },
    },
    // {
    //   title: "Current Location",
    //   dataIndex: "location",
    //   width: "220px",
    //   render: (_, record) => {
    //     return record.location?.coordinates.join(", ");
    //   },
    // },
    {
      title: "MFG Date",
      dataIndex: "mfg_date",
      width: "150px",
      render: (_, record) => {
        return record.mfg_date
          ? moment(record.mfg_date).format("DD-MM-YYYY HH:mm")
          : "Not Given";
      },
    },
    {
      title: "Lock Status",
      dataIndex: "lock_status",
      fixed: "right",
      width: 150,
      filters: [
        {
          text: "Locked",
          value: true,
        },
        {
          text: "Un Locked",
          value: false,
        },
      ],
      onFilter: (value, record) => record.lock_status == value,
      render: (_, record) => {
        return (
          <>
            <div>
              <Tag color={record.lock_status ? "red" : "green"}>
                {record.lock_status ? "Locked" : "Un Locked"}
              </Tag>
              {allowEdit ? (
                <div style={{ marginTop: 5 }}>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      handleBatteryLock(record._id, !record.lock_status);
                    }}
                  >
                    {`${record.lock_status ? "Un Lock" : "Lock"} It`}
                  </Button>
                </div>
              ) : null}
            </div>
          </>
        );
      },
    },
  ];

  const handleRowCanceled = () => {
    setShowBatteryModal(false);
  };

  const handleRowEdit = (idx) => {
    setEditingKey(idx);
    setShowBatteryModal(true);
  };

  const handleRowSave = async (row) => {
    if (editingKey == null) {
      const res = await batteryApiRequest({
        path: apiPaths.batteries,
        method: "POST",
        data: row,
        token: user.token,
      });
      if (res.error) {
        message.error(res.error_description);
        return;
      }
      batteries[res._id] = res;
      setBatteries({
        [res._id]: res,
        ...batteries,
      });
      setShowBatteryModal(false);
      return;
    }
    let key = editingKey;
    const res = await batteryApiRequest({
      path: apiPaths.batteries + `/${key}`,
      method: "PUT",
      data: row,
      token: user.token,
    });
    if (res.error) {
      message.error(res.error_description);
      return;
    }
    batteries[editingKey] = res;
    setBatteries({ ...batteries });
    setEditingKey(null);
    setShowBatteryModal(false);
    setShowValidityModal(false);
  };

  return (
    <div
      style={{
        backgroundColor: "#fff",
        width: "100%",
        height: "100%",
        display: "flex",
      }}
    >
      <div style={{ width: "60%", height: "100%", padding: "1% 2%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginRight: 10,
          }}
        >
          <div style={{ fontSize: 20, fontWeight: "bold" }}>Batteries</div>
          {allowEdit ? (
            <Space>
              <Tooltip title="Add Battery">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingKey(null);
                    setShowBatteryModal(true);
                  }}
                />
              </Tooltip>
            </Space>
          ) : null}
        </div>
        <div style={{ height: "90%", marginTop: 30 }}>
          <DynamicTable
            dataSource={Object.values(batteries)}
            columns={COLUMNS}
            defaultColumns={DEFAULT_COLUMNS}
            pagination={false}
            requireEdit={allowEdit}
            showActionColumn={allowEdit}
            scroll={{ y: "65vh" }}
            editHandler={(record) => handleRowEdit(record._id)}
          />
        </div>
        {/* {qrCodeData ? (
        <div
          style={{
            position: "absolute",
            right: -300,
            backgroundColor: "#fff",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: 200,
            height: 230,
          }}
          ref={(_ref) => (ref.current = _ref)}
        >
          <div style={{ marginBottom: 10, fontSize: 18, fontWeight: "bold" }}>
            Scan to Unlock <UnlockFilled />
          </div>
          <div
            style={{
              height: 170,
              width: 170,
            }}
          >
            <QRCode
              style={{ width: "100%", height: "100%" }}
              value={qrCodeData}
            />
          </div>
        </div>
      ) : null} */}
        {showBatteryModal ? (
          <BatteryModal
            open={showBatteryModal}
            onSave={handleRowSave}
            onCancel={handleRowCanceled}
            data={batteries[editingKey]}
          />
        ) : null}
        {showValidityModal ? (
          <ValidityModal
            open={showValidityModal}
            onSave={handleRowSave}
            onCancel={handleRowCanceled}
            data={batteries[editingKey]}
          />
        ) : null}
      </div>
      <div style={{ width: "40%", height: "100vh" }}>
        <MapContainer
          center={[21.1667, 77.833298]}
          zoom={5}
          style={{ width: "100%", height: "100%" }}
          // scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {/* {renderBatteries(batteries)} */}
          <BatteryMarkers
            activeBattery={activeBattery}
            batteries={Object.values(batteries)}
          />
        </MapContainer>
      </div>
    </div>
  );
}

export default Batteries;
